import type { AriaAttributes, ReactNode } from 'react';

import classNames from 'classnames';

import styles from './Hero.module.css';

import { Button } from '../Button';
import { PageWidth } from '../PageWidth';
import type { TextProps } from '../Text';
import { Text } from '../Text';

export interface HeroProps extends Pick<
  AriaAttributes,
  'aria-label' | 'aria-labelledby'
> {
  /**
   * The title content. Please use `aria-label` or `aria-labelledby` to name the
   * section.
   */
  title?: ReactNode;
  /** Props to pass to the title text component */
  titleProps?: TextProps;
  /** The subtitle content */
  subtitle?: ReactNode;
  /** Props to pass to the subtitle text component */
  subtitleProps?: TextProps;
  /** A link path for the call-to-action */
  linkTo?: string;
  /** A link path for the call-to-action */
  linkLabel?: string;
  /** The background image for the hero */
  background: ReactNode;
  /** CSS className */
  className?: string;
}

/**
 * Render an "hero image" section to display a title, subtitle and a
 * call-to-action.
 *
 * @see https://corgi-x.tfd.engineering/components/hero
 */
export function Hero(props: HeroProps) {
  const {
    title,
    titleProps,
    subtitle,
    subtitleProps,
    linkTo,
    linkLabel,
    background,
    className,
    ...rootProps
  } = props;
  return (
    <section className={classNames(styles.section, className)} {...rootProps}>
      <div className={styles.background}>{background}</div>
      <PageWidth className={styles.content}>
        {title && (
          <Text
            as="h1"
            color="White"
            bold
            className={styles.title}
            variant="heading-52"
            {...titleProps}
          >
            {title}
          </Text>
        )}
        {subtitle && (
          <Text
            as="h2"
            color="White"
            bold
            className={styles.subtitle}
            variant="heading-22"
            {...subtitleProps}
          >
            {subtitle}
          </Text>
        )}
        {linkTo && linkLabel && (
          <Button type="link" to={linkTo} className={styles.cta}>
            {linkLabel}
          </Button>
        )}
      </PageWidth>
    </section>
  );
}
